import { FC } from 'react';
import imageCompression, { Options } from 'browser-image-compression';
import { IUploadFile } from '../store/models/general-models';
import { requestFileFieldType } from '../views/authorized/pages/requests/types';
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  SUPPORTED_FILE_FORMATS,
} from '../constants';
import { ReactComponent as docIcon } from 'assets/icons/file_doc.svg';
import { ReactComponent as imgIcon } from 'assets/icons/file_image.svg';
import { ReactComponent as pdfIcon } from 'assets/icons/file_pdf.svg';
import { ReactComponent as rarIcon } from 'assets/icons/file_rar.svg';
import { ReactComponent as unknownIcon } from 'assets/icons/file_unknown.svg';
import { ReactComponent as xlsIcon } from 'assets/icons/file_xls.svg';
import { unknown } from '../assets/icons';
import doc from '../assets/icons/file_doc.svg';
import img from '../assets/icons/file_image.svg';
import pdf from '../assets/icons/file_pdf.svg';
import rar from '../assets/icons/file_rar.svg';
import xls from '../assets/icons/file_xls.svg';

import { errorToast } from './toast';

const SupportedFormats: Record<string, string> = {
  pdf: pdf,
  doc: doc,
  rar: rar,
  xls: xls,
  xlsx: xls,
  jpg: img,
  jpeg: img,
  png: img,
  gif: img,
  webp: img,
  bmp: img,
  svg: img,
  // TODO ...
  mp4: img,
};

/* Подставляем иконку файла соответственно формату (из названия) */
export const getFileLogo = (name: string): string => {
  for (const format of Object.keys(SupportedFormats)) {
    if (name?.includes(format)) {
      return SupportedFormats[format];
    }
  }

  return unknown;
};

const SupportedFormatsIcons: Record<string, FC> = {
  pdf: pdfIcon,
  doc: docIcon,
  rar: rarIcon,
  xls: xlsIcon,
  xlsx: xlsIcon,
  jpg: imgIcon,
  jpeg: imgIcon,
  png: imgIcon,
  gif: imgIcon,
  webp: imgIcon,
  bmp: imgIcon,
  svg: imgIcon,
  // TODO ...
  mp4: imgIcon,
};

export const getFileIcon = (name: string): FC => {
  const supportedKeys = Object.keys(SupportedFormats);

  for (let index = 0; index < supportedKeys.length; index++) {
    const format = supportedKeys[index];

    if (name?.includes(format)) {
      return SupportedFormatsIcons[format];
    }
  }

  return unknownIcon;
};

const EXTENSION_BY_IMAGE_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
};

/** Имя файла с расширением. У вложений-фото из мессенджера имени нет и вместо
 * него подставляется id вложения (например, "35585") — без расширения файл не
 * определяется как картинка (иконка, формат в превью), поэтому достраиваем его
 * по MIME-типу. Имя с расширением и неизвестный тип возвращаются как есть. */
export const ensureFileExtension = (name: string, mimeType: string): string => {
  if (/\.[a-z0-9]+$/i.test(name)) {
    return name;
  }

  const extension = EXTENSION_BY_IMAGE_MIME_TYPE[mimeType.toLowerCase()];

  return extension ? `${name}.${extension}` : name;
};

// Категория файла по MIME-типу — используется и при стейджинге вложений перед
// отправкой (houseChatsStore, message-sender), и при их рендере в сообщении
export const categorizeFileType = (
  file: File
): 'image' | 'video' | 'audio' | 'file' => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }

  if (file.type.startsWith('video/')) {
    return 'video';
  }

  return 'file';
};

export const copyArrayBuffer = (src: any) => {
  const dst = new ArrayBuffer(src.byteLength);
  new Uint8Array(dst).set(new Uint8Array(src));

  return dst;
};

export const compressImg = async (file: File, options: Options) => {
  return await imageCompression(file, options);
};

function readAsArrayBuffer(file: File) {
  return new Response(file).arrayBuffer();
}

export const compareFiles = async (firstFile: File, secondFile: File) => {
  if (firstFile.size !== secondFile.size) {
    return Promise.resolve(false);
  }

  return Promise.all([
    readAsArrayBuffer(firstFile),
    readAsArrayBuffer(secondFile),
  ]).then(([buf1, buf2]) => {
    const arr1 = new Uint8Array(buf1);
    const arr2 = new Uint8Array(buf2);

    return !arr1.some(
      (val, i) => arr2[i] !== val // поиск отличий
    );
  });
};

export const handleDownloadFile = (file: File, fileName: string) => {
  const blob = new Blob([file], { type: file.type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.href = url;
  a.download = fileName;
  a.target = '_self';
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const validateRequestFilesFormat = (MIMEType: string) => {
  return (
    [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
      'application/vnd.ms-excel.addin.macroenabled.12',
      'application/vnd.ms-excel.sheet.macroenabled.12',
      'application/vnd.ms-excel.sheet.binary.macroenabled.12',
      'application/vnd.ms-excel.template.macroenabled.12',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.ms-excel',
      'application/rtf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
    ].includes(MIMEType.toLowerCase()) ||
    MIMEType.toLowerCase().includes('video') // для видео реализовано конвертирование к одному формату
  );
};

export const validateDocumentsFlowFilesFormat = (MIMEType: string) => {
  return [
    'text/csv',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.addin.macroenabled.12',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/vnd.ms-excel.sheet.binary.macroenabled.12',
    'application/vnd.ms-excel.template.macroenabled.12',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.ms-excel',
    'application/rtf',
    'image/jpeg',
    'image/png',
  ].includes(MIMEType.toLowerCase());
};

/*
  Попытка реализовать First Fit Decreasing algorithm.
  Учитывая количество предметов разного веса и контейнеров емкостью "c", необходимо назначить каждый предмет
  контейнеру так, чтобы общее количество используемых контейнеров было минимизировано. В нашем случае, например, есть
  ограничение на посылку form-data (до 250 Мб) и N-ное количество файлов со своими размерами.
  Например [150 Мб, 50 Мб, 100 Мб, 100 Мб, 20Мб ] и ограничении 250 Мб должены быть разбиты на 2 контейнера
  [[150 Мб, 100 Мб], [50 Мб, 100 Мб, 20 Мб]]. Реализовано частично (возвращает только оптимальное количество
  контейнеров), + иногда всё равно разбивает неоптимально, например [50, 40, 40, 20, 20, 30] и ограничении 100,
  разбивает на 3 контейнера а не на 2.
*/
export const firstFitDec = (weight: number[], c: number) => {
  let res = 0;
  const binRem = weight.map(() => 0);
  weight.sort().reverse();

  for (let i: number = 0; i < weight.length; i++) {
    let j = 0;

    while (j < res) {
      if (binRem[j] >= weight[i]) {
        binRem[j] = binRem[j] - weight[i];
        break;
      }

      j += 1;
    }

    if (j === res) {
      binRem[res] = c - weight[i];
      res += 1;
    }
  }

  return res;
};

export const prepareToDownloadAndTransform = (
  fileId: string,
  fileName: string,
  field: requestFileFieldType | 'files',
  toDownloadAndTransformArray: any[],
  endpoint: string
) => {
  const link = `${window.location.protocol}//${window.location.host}/${endpoint}`;

  toDownloadAndTransformArray.push({
    url: link,
    otherInformation: {
      fileId,
      fileName,
      field,
      index: toDownloadAndTransformArray.length || 0,
    },
  });
};

/**
 * Возвращает строку MIME-типов на основе переданных флагов.
 * Если флаги не установлены, возвращает дефолтное значение.
 * @param isPhotoUploader - принимать ли изображения
 * @param isVideoUploader - принимать ли видео
 * @returns строка MIME-типов для input[type="file"].
 */
export const getAcceptTypes = (
  isPhotoUploader: boolean,
  isVideoUploader: boolean
): string => {
  const types: string[] = [];

  if (isPhotoUploader) {
    types.push(SUPPORTED_FILE_FORMATS.IMAGE_MIME_TYPES);
  }

  if (isVideoUploader) {
    types.push(SUPPORTED_FILE_FORMATS.VIDEO_MIME_TYPES);
  }

  if (!isPhotoUploader && !isVideoUploader) {
    return SUPPORTED_FILE_FORMATS.DEFAULT_MIME_TYPES;
  }

  return types.join(', ');
};

/**
 * Фильтрует список новых файлов, исключая файлы, которые превышают максимально допустимый размер
 * или уже существуют в списке загруженных файлов.
 *
 * @param {File[]} newFiles - Массив новых файлов для проверки.
 * @param {IUploadFile[]} existsFiles - Массив уже загруженных файлов для проверки на дубликаты.
 * @returns {File[]} Отфильтрованный массив файлов, которые прошли проверку.
 *
 * @example
 * const validFiles = validateFiles(newFiles, existingFiles);
 *
 * if (validFiles.length > 0) {
 *   // Выполнить загрузку файлов
 * }
 */
export const validateFiles = (
  newFiles: File[],
  existsFiles: IUploadFile[]
): File[] => {
  return newFiles.filter((file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      errorToast(`${file.name} превышает размер в ${MAX_FILE_SIZE_MB} МБ`);

      return false;
    }

    if (existsFiles.some((existFile) => existFile.name === file.name)) {
      errorToast(`Файл с именем ${file.name} уже добавлен.`);

      return false;
    }

    return true;
  });
};

/**
 * Загружает массив файлов и обрабатывает статус их загрузки параллельно.
 *
 * @param {IUploadFile[]} validFiles - Массив файлов для загрузки.
 * @param {(file: File) => Promise<Partial<IUploadFile>>} onUpload - Колбэк для загрузки файла.
 * @param {(fileName: string) => void} [onError] - Колбэк, вызываемый при ошибочной загрузки файла.
 * @returns {Promise<void>} Промис, который разрешается после завершения всех загрузок.
 */
export const handleUploadFiles = async (
  validFiles: IUploadFile[],
  onUpload: (file: File) => Promise<Partial<IUploadFile>>,
  onError: (fileName: string) => void
): Promise<void> => {
  for (const file of validFiles) {
    try {
      await file.upload(onUpload);
    } catch (error) {
      onError?.(file.name);
      errorToast(`Ошибка загрузки файла ${file.name}`);
    }
  }
};

export const downloadBlobFile = async (
  blob: Blob,
  filename: string = 'downloaded_file'
) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
